import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Building2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    business_type: "gym",
    admin_name: "",
    admin_email: "",
    admin_phone: "",
    admin_password: ""
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BACKEND_URL}/api/superuser/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${BACKEND_URL}/api/superuser/companies`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Company created successfully");
      fetchCompanies();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tenant");
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm("Are you sure you want to deactivate this tenant?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/api/superuser/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Company deactivated successfully");
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to deactivate tenant");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      phone: "",
      email: "",
      business_type: "gym",
      admin_name: "",
      admin_email: "",
      admin_phone: "",
      admin_password: ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Company Management
          </h1>
          <p className="text-zinc-500">Manage all companies and their administrators</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
              <Plus size={20} className="mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" aria-describedby="tenant-dialog-description">
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
            </DialogHeader>
            <p id="tenant-dialog-description" className="sr-only">
              Create a new company with admin user
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="border-b pb-4">
                <h3 className="font-bold text-lg mb-3">Company Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., IronPulse Gym"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      placeholder="Full address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      placeholder="+1234567890"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="company@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="business_type">Business Type *</Label>
                    <Select
                      value={formData.business_type}
                      onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gym">Gym</SelectItem>
                        <SelectItem value="yoga">Yoga Studio</SelectItem>
                        <SelectItem value="zumba">Zumba Classes</SelectItem>
                        <SelectItem value="pilates">Pilates Studio</SelectItem>
                        <SelectItem value="crossfit">CrossFit Box</SelectItem>
                        <SelectItem value="martial_arts">Martial Arts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-3">Admin User</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="admin_name">Admin Name *</Label>
                    <Input
                      id="admin_name"
                      placeholder="John Doe"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_email">Admin Email *</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      placeholder="admin@example.com"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_phone">Admin Phone *</Label>
                    <Input
                      id="admin_phone"
                      placeholder="+1234567890"
                      value={formData.admin_phone}
                      onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="admin_password">Admin Password *</Label>
                    <Input
                      id="admin_password"
                      type="password"
                      placeholder="Secure password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              >
                Create Company
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.length === 0 ? (
          <div className="col-span-full text-center py-12 text-zinc-500">
            No companies found. Create your first company to get started.
          </div>
        ) : (
          companies.map((company) => (
            <div
              key={company.id}
              className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center">
                    <Building2 size={24} className="text-lime-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {company.name}
                    </h3>
                    <Badge className={`mt-1 ${
                      company.status === 'active'
                        ? 'bg-lime-100 text-lime-800'
                        : 'bg-zinc-100 text-zinc-800'
                    }`}>
                      {company.status}
                    </Badge>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(company.id)}
                  className="p-2 hover:bg-red-50 rounded-md transition-colors"
                  title="Deactivate"
                >
                  <Trash2 size={16} className="text-red-600" />
                </button>
              </div>

              <div className="space-y-2 text-sm text-zinc-600">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-zinc-400" />
                  <span className="truncate">{company.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-zinc-400" />
                  <span className="truncate">{company.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-zinc-400" />
                  <span>{company.phone}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-zinc-200 flex justify-between items-center">
                <span className="text-xs text-zinc-500 capitalize">
                  {company.business_type.replace('_', ' ')}
                </span>
                <span className="text-xs text-zinc-500">
                  {company.admin_count || 0} admin(s)
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CompanyManagement;
